import * as d3 from "../d3.js";
import { isElementPaintVisible } from "./presentation-render-element.js";
import {
    PROVENANCE_CALLOUT_ID,
    provenanceAriaLabel,
    serializeProvenanceClaims,
} from "./provenance-indicator.js";
import {
    LOOT_ITEM_ROW_LAYOUT,
    lootItemRowMarkup,
} from "./loot-item-row.js";
import {
    DEFAULT_CALCULATOR_LOOT_SORT,
    parseCalculatorLootSort,
    sortCalculatorLootRows,
} from "./calculator-loot-sort.js";
import {
    calculatorMetricTextWithUnit,
} from "./calculator-metric-unit-mark.js";
import { setStatBreakdownPresentation } from "./stat-breakdown-tooltip.js";

const TOP_PADDING = 20;
const BOTTOM_PADDING = 20;
const GROUP_GAP = 12;
const SPECIES_LABEL_GAP = 8;
const LEFT_X = 8;
const LEFT_WIDTH = 200;
const RIGHT_BAR_WIDTH = 18;
const RIGHT_LABEL_WIDTH = LOOT_ITEM_ROW_LAYOUT.sankeyWidth;
const RIGHT_LABEL_HEIGHT = LOOT_ITEM_ROW_LAYOUT.height;
const RIGHT_LABEL_OFFSET = 14;
const SPECIES_BOX_CONNECTOR_INSET = 8;
const GROUP_TO_SPECIES_GAP = 24;
const SPECIES_TO_RIGHT_GAP = 78;
const RIGHT_TO_GROUP_GAP = 24;
const RIGHT_GROUP_WIDTH = 212;
const RIGHT_MARGIN = 8;
const NODE_RADIUS = 12;
const MIN_INTERNAL_WIDTH =
    LEFT_X
    + LEFT_WIDTH
    + GROUP_TO_SPECIES_GAP
    + RIGHT_BAR_WIDTH
    + RIGHT_LABEL_OFFSET
    + RIGHT_LABEL_WIDTH
    + SPECIES_TO_RIGHT_GAP
    + RIGHT_BAR_WIDTH
    + RIGHT_TO_GROUP_GAP
    + RIGHT_GROUP_WIDTH
    + RIGHT_MARGIN;
const PROVENANCE_RAIL_WIDTH = 7;
const PROVENANCE_RAIL_HIT_WIDTH = 24;
const PROVENANCE_RAIL_INSET = 8;
const PROVENANCE_RAIL_GAP = 1.5;
const GROUP_PROVENANCE_RAIL_MAX_HEIGHT = 38;

export function lootSankeyItemMarkup(row, labels) {
    return lootItemRowMarkup({
        rowId: row.rowId,
        item: {
            label: row.label,
            iconUrl: row.iconUrl,
            iconGradeTone: row.iconGradeTone,
            priceText: row.priceText,
            priceLabel: labels.basePriceLabel,
            quantityText: row.quantityText,
            quantityLabel: row.quantityLabel,
            control: {
                type: "static",
                ariaLabel: row.label,
            },
        },
        leadingMetric: {
            primary: {
                text: calculatorMetricTextWithUnit(row.rateDisplay),
                unitMark: row.rateDisplay.unitMark,
                state: row.rateDisplay.state,
            },
            secondary: {
                text: row.leftText,
                unitMark: row.leftMetricUnitMark,
            },
            tone: {
                fillColor: row.groupFillColor,
                strokeColor: row.groupStrokeColor,
                textColor: row.groupTextColor,
            },
            control: {
                type: "static",
                ariaLabel: `${row.label} · ${metricDisplayAriaText(
                    row.rateDisplay,
                    labels.rateBasisLabels,
                )}, ${labels.leftMetricLabel}: ${row.leftText}`,
                breakdownId: row.leftBreakdown?.id ?? null,
            },
        },
        trailingMetric: {
            primary: {
                text: calculatorMetricTextWithUnit(row.rightShareDisplay),
                unitMark: row.rightShareDisplay.unitMark,
            },
            secondary: {
                text: row.rightCompactText,
                unitMark: row.rightMetricUnitMark,
            },
            tone: {
                fillColor: row.groupFillColor,
                strokeColor: row.groupStrokeColor,
                textColor: row.groupTextColor,
            },
            control: {
                type: "static",
                ariaLabel: `${row.label} · ${metricDisplayAriaText(
                    row.rightShareDisplay,
                    labels.rateBasisLabels,
                )}, ${labels.rightMetricLabel}: ${row.rightText}`,
                breakdownId: row.rightBreakdown?.id ?? null,
            },
        },
        provenanceSegments: row.provenanceSegments,
        provenanceLabel: labels.provenanceLabel,
    });
}

function visualWeight(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

export function lootSankeyGroupLayout(rows, valueKey, totalSpan, gap = GROUP_GAP) {
    const normalizedRows = Array.isArray(rows) ? rows : [];
    const span = visualWeight(totalSpan);
    if (!normalizedRows.length || span <= 0) {
        return [];
    }

    const usableSpan = Math.max(0, span - gap * Math.max(0, normalizedRows.length - 1));
    const values = normalizedRows.map((row) => visualWeight(row?.[valueKey]));
    const total = d3.sum(values);
    let cursor = 0;

    return normalizedRows.map((row, index) => {
        const height = total > 0 ? (values[index] / total) * usableSpan : 0;
        const entry = {
            rowId: row.rowId,
            top: cursor,
            height,
        };
        cursor += height + gap;
        return entry;
    });
}

export function lootSankeySpeciesLayout(rows, speciesRows, groupLayout, valueKey) {
    const normalizedSpeciesRows = Array.isArray(speciesRows) ? speciesRows : [];
    const result = Array.from({ length: normalizedSpeciesRows.length }, () => ({
        top: 0,
        height: 0,
    }));
    const layoutByGroup = new Map(
        (Array.isArray(groupLayout) ? groupLayout : []).map((entry) => [entry.rowId, entry]),
    );
    const indicesByGroup = new Map();

    normalizedSpeciesRows.forEach((row, index) => {
        const groupRowId = row.groupRowId;
        if (!groupRowId || !layoutByGroup.has(groupRowId)) {
            return;
        }
        if (!indicesByGroup.has(groupRowId)) {
            indicesByGroup.set(groupRowId, []);
        }
        indicesByGroup.get(groupRowId).push(index);
    });

    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const group = layoutByGroup.get(row.rowId);
        const indices = indicesByGroup.get(row.rowId) ?? [];
        if (!group || !indices.length) {
            return;
        }
        const values = indices.map((index) =>
            visualWeight(normalizedSpeciesRows[index]?.[valueKey]),
        );
        const total = d3.sum(values);
        let cursor = group.top;
        indices.forEach((speciesIndex, localIndex) => {
            const height = total > 0 ? (values[localIndex] / total) * group.height : 0;
            result[speciesIndex] = {
                top: cursor,
                height,
            };
            cursor += height;
        });
    });

    return result;
}

function sankeyPath(x1, y1, x2, y2, h1, h2) {
    const c1 = x1 + 120;
    const c2 = x2 - 120;
    return [
        `M ${x1} ${y1}`,
        `C ${c1} ${y1}, ${c2} ${y2}, ${x2} ${y2}`,
        `L ${x2} ${y2 + h2}`,
        `C ${c2} ${y2 + h2}, ${c1} ${y1 + h1}, ${x1} ${y1 + h1}`,
        "Z",
    ].join(" ");
}

function bindMetricBreakdown(selection, breakdown, ariaLabel) {
    selection
        .attr("tabindex", 0)
        .attr("focusable", true)
        .attr("aria-label", ariaLabel)
        .attr("data-calculator-breakdown-id", breakdown.id)
        .style("cursor", "help");
    setStatBreakdownPresentation(selection.node(), breakdown);
    return selection;
}

function bindProvenancePresentation(selection, segment) {
    const claims = serializeProvenanceClaims(segment.claims);
    return selection
        .attr("data-fishy-provenance-claims", claims);
}

function truncateSankeyText(value, maximumLength = 28) {
    const text = String(value ?? "");
    return text.length > maximumLength
        ? `${text.slice(0, Math.max(0, maximumLength - 1))}…`
        : text;
}

function metricDisplayAriaText(display, rateBasisLabels = {}) {
    const text = display?.titleText ?? calculatorMetricTextWithUnit(display);
    if (display?.state !== "known" || !display?.basis) {
        return text;
    }
    if (display.basis === "percent") {
        return text;
    }
    const label = String(rateBasisLabels[display.basis] ?? "").trim();
    return label ? `${text} ${label}` : text;
}

function estimatedSvgTextWidth(text, fontSize) {
    return [...String(text ?? "")].length * Number.parseFloat(fontSize) * 0.56;
}

function appendMetricShareDisplay(
    selection,
    display,
    {
        x,
        y,
        color,
        fontSize,
        fontWeight,
        secondaryText = "",
        centered = false,
    },
) {
    const text = calculatorMetricTextWithUnit(display);
    if (centered && !secondaryText) {
        selection.append("text")
            .attr("x", x)
            .attr("y", y)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("data-calculator-metric-share-value", "")
            .style("fill", color)
            .style("font-size", fontSize)
            .style("font-weight", fontWeight)
            .style("font-variant-numeric", "tabular-nums")
            .text(text);
        return;
    }

    selection.append("text")
        .attr("x", x)
        .attr("y", y)
        .attr("dominant-baseline", "middle")
        .attr("data-calculator-metric-share-value", "")
        .style("fill", color)
        .style("font-size", fontSize)
        .style("font-weight", fontWeight)
        .style("font-variant-numeric", "tabular-nums")
        .text(text);
    const cursor = x + estimatedSvgTextWidth(text, fontSize) + 5;
    if (secondaryText) {
        selection.append("text")
            .attr("x", cursor)
            .attr("y", y)
            .attr("dominant-baseline", "middle")
            .style("fill", color)
            .style("font-size", fontSize)
            .style("font-weight", fontWeight)
            .style("font-variant-numeric", "tabular-nums")
            .text(`· ${secondaryText}`);
    }
}

export function sortLootSankeyItemRows(items, groups, sort = DEFAULT_CALCULATOR_LOOT_SORT) {
    const rows = Array.isArray(items) ? items : [];
    const groupRows = Array.isArray(groups) ? groups : [];
    const parsedSort = parseCalculatorLootSort(sort);
    if (parsedSort.by === "database") {
        return sortCalculatorLootRows(rows, parsedSort);
    }
    const byGroup = new Map();
    for (const row of rows) {
        const groupRowsForItem = byGroup.get(row.groupRowId) ?? [];
        groupRowsForItem.push(row);
        byGroup.set(row.groupRowId, groupRowsForItem);
    }
    const ordered = groupRows.flatMap((group) => (
        sortCalculatorLootRows(byGroup.get(group.rowId) ?? [], parsedSort)
    ));
    const knownRows = new Set(ordered);
    return ordered.concat(sortCalculatorLootRows(
        rows.filter((row) => !knownRows.has(row)),
        parsedSort,
    ));
}

export class FishyLootSankey extends HTMLElement {
    constructor() {
        super();
        this._rows = null;
        this._sort = DEFAULT_CALCULATOR_LOOT_SORT;
        this._rafId = 0;
        this._resizeObserver = null;
    }

    get rows() {
        return this._rows;
    }

    set rows(value) {
        if (value !== null) {
            const fields = Object.keys(value ?? {}).sort();
            if (
                fields.length !== 3
                || fields[0] !== "groups"
                || fields[1] !== "items"
                || fields[2] !== "labels"
                || !Array.isArray(value.groups)
                || !Array.isArray(value.items)
                || !value.labels
                || typeof value.labels !== "object"
            ) {
                throw new TypeError("loot Sankey rows must contain exactly groups, items, and labels");
            }
        }
        this._rows = value;
        this.scheduleRender();
    }

    get sort() {
        return this._sort;
    }

    set sort(value) {
        const parsed = parseCalculatorLootSort(value);
        if (parsed.by === this._sort.by && parsed.order === this._sort.order) {
            return;
        }
        this._sort = parsed;
        this.scheduleRender();
    }

    connectedCallback() {
        for (const property of ["rows", "sort"]) {
            if (!Object.prototype.hasOwnProperty.call(this, property)) {
                continue;
            }
            const value = this[property];
            delete this[property];
            this[property] = value;
        }
        this._resizeObserver = new ResizeObserver(() => this.scheduleRender());
        this._resizeObserver.observe(this);
        this.scheduleRender();
    }

    disconnectedCallback() {
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        if (this._rafId) {
            cancelAnimationFrame(this._rafId);
            this._rafId = 0;
        }
    }

    scheduleRender() {
        if (!this.isConnected || this._rafId) {
            return;
        }
        this._rafId = requestAnimationFrame(() => {
            this._rafId = 0;
            this.render();
        });
    }

    replaceRenderedChildren(...nodes) {
        this.replaceChildren(...nodes);
    }

    render() {
        if (!isElementPaintVisible(this)) {
            return;
        }
        if (!this._rows) {
            this.replaceRenderedChildren();
            return;
        }
        const { groups: rows, items, labels } = this._rows;
        const speciesRows = sortLootSankeyItemRows(items, rows, this._sort);
        if (!rows.length || !speciesRows.length) {
            this.replaceRenderedChildren();
            return;
        }

        const labelStackHeight = speciesRows.length
            ? speciesRows.length * RIGHT_LABEL_HEIGHT
                + Math.max(0, speciesRows.length - 1) * SPECIES_LABEL_GAP
            : 0;
        const innerHeight = Math.max(labelStackHeight, 340);
        const width = Math.max(this.clientWidth || 0, MIN_INTERNAL_WIDTH);
        const leftBarX = LEFT_X + LEFT_WIDTH + GROUP_TO_SPECIES_GAP;
        const rightGroupX = width - RIGHT_MARGIN - RIGHT_GROUP_WIDTH;
        const rightBarX = rightGroupX - RIGHT_TO_GROUP_GAP - RIGHT_BAR_WIDTH;
        const leftSpeciesX = leftBarX + RIGHT_BAR_WIDTH + RIGHT_LABEL_OFFSET;
        const rightSpeciesX = rightBarX - SPECIES_TO_RIGHT_GAP - RIGHT_LABEL_WIDTH;
        const labelX = (leftSpeciesX + rightSpeciesX) / 2;
        const height = innerHeight + TOP_PADDING + BOTTOM_PADDING;
        const leftGroupLayout = lootSankeyGroupLayout(
            rows,
            "leftWeight",
            innerHeight,
            GROUP_GAP,
        );
        const leftSpeciesLayout = lootSankeySpeciesLayout(
            rows,
            speciesRows,
            leftGroupLayout,
            "leftWeight",
        );
        const rightGroupLayout = lootSankeyGroupLayout(
            rows,
            "rightWeight",
            innerHeight,
            GROUP_GAP,
        );
        const rightSpeciesLayout = lootSankeySpeciesLayout(
            rows,
            speciesRows,
            rightGroupLayout,
            "rightWeight",
        );
        const leftGroupTop = new Map(
            leftGroupLayout.map((entry) => [entry.rowId, TOP_PADDING + entry.top]),
        );
        const leftGroupHeights = new Map(
            leftGroupLayout.map((entry) => [entry.rowId, entry.height]),
        );
        const rightGroupTop = new Map(
            rightGroupLayout.map((entry) => [entry.rowId, TOP_PADDING + entry.top]),
        );
        const rightGroupHeights = new Map(
            rightGroupLayout.map((entry) => [entry.rowId, entry.height]),
        );

        const speciesLabelTop = [];
        let labelCursor = TOP_PADDING;
        speciesRows.forEach(() => {
            speciesLabelTop.push(labelCursor);
            labelCursor += RIGHT_LABEL_HEIGHT + SPECIES_LABEL_GAP;
        });

        const svg = d3
            .create("svg")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("role", "group")
            .attr("aria-label", labels.ariaLabel);

        const leftFlows = svg.append("g");
        speciesRows.forEach((row, index) => {
            const leftLayout = leftSpeciesLayout[index];
            const leftTop = TOP_PADDING + leftLayout.top;
            const leftHeight = leftLayout.height;
            const rightTop = leftTop;
            const rightHeight = leftHeight;

            leftFlows.append("path")
                .attr("data-calculator-row-id", row.rowId)
                .attr(
                    "d",
                    sankeyPath(
                        LEFT_X + LEFT_WIDTH,
                        leftTop,
                        leftBarX,
                        rightTop,
                        leftHeight,
                        rightHeight,
                    ),
                )
                .style("fill", row.groupConnectorColor)
                .style("opacity", 0.42);
        });

        const rightFlows = svg.append("g");
        speciesRows.forEach((row, index) => {
            const leftLayout = leftSpeciesLayout[index];
            const rightLayout = rightSpeciesLayout[index];
            const leftTop = TOP_PADDING + leftLayout.top;
            const leftHeight = leftLayout.height;
            const rightHeight = rightLayout.height;
            const speciesRightTop = TOP_PADDING + rightLayout.top;
            rightFlows.append("path")
                .attr("data-calculator-row-id", row.rowId)
                .attr(
                    "d",
                    sankeyPath(
                        leftBarX + RIGHT_BAR_WIDTH,
                        leftTop,
                        rightBarX,
                        speciesRightTop,
                        leftHeight,
                        rightHeight,
                    ),
                )
                .style("fill", row.groupConnectorColor)
                .style("opacity", 0.28);

            rightFlows.append("path")
                .attr("data-calculator-row-id", row.rowId)
                .attr(
                    "d",
                    sankeyPath(
                        rightBarX + RIGHT_BAR_WIDTH,
                        speciesRightTop,
                        rightGroupX,
                        speciesRightTop,
                        rightHeight,
                        rightHeight,
                    ),
                )
                .style("fill", row.groupConnectorColor)
                .style("opacity", 0.44);
        });

        const leftNodes = svg.append("g");
        rows.forEach((row) => {
            const top = leftGroupTop.get(row.rowId);
            const heightValue = leftGroupHeights.get(row.rowId);
            const mid = top + heightValue / 2;
            const hasCondition = Boolean(row.conditionText);
            const labelY = mid - (hasCondition ? 15 : 8);
            const valueY = mid + (hasCondition ? 1 : 10);
            const provenanceSegments = row.provenanceSegments;
            const provenanceRailX =
                LEFT_X + LEFT_WIDTH - PROVENANCE_RAIL_INSET - PROVENANCE_RAIL_WIDTH;
            const availableRailHeight = Math.max(0, heightValue - 8);
            const provenanceRailHeight = Math.min(
                GROUP_PROVENANCE_RAIL_MAX_HEIGHT,
                availableRailHeight,
            );
            const provenanceRailY = top + Math.max(0, (heightValue - provenanceRailHeight) / 2);
            const provenanceSegmentHeight = provenanceSegments.length
                ? Math.max(
                    0,
                    provenanceRailHeight - PROVENANCE_RAIL_GAP * (provenanceSegments.length - 1),
                ) / provenanceSegments.length
                : 0;

            const groupNode = bindMetricBreakdown(
                leftNodes.append("g").attr("data-calculator-row-id", row.rowId),
                row.leftBreakdown,
                `${row.label} · ${metricDisplayAriaText(
                    row.leftShareDisplay,
                    labels.rateBasisLabels,
                )}, ${labels.leftMetricLabel}: ${row.leftText}`,
            );

            groupNode.append("rect")
                .attr("x", LEFT_X)
                .attr("y", top)
                .attr("width", LEFT_WIDTH)
                .attr("height", heightValue)
                .attr("rx", NODE_RADIUS)
                .attr("ry", NODE_RADIUS)
                .style("fill", row.fillColor)
                .style("stroke", row.strokeColor)
                .style("stroke-width", 1.5);

            groupNode.append("text")
                .attr("x", LEFT_X + 10)
                .attr("y", labelY)
                .attr("dominant-baseline", "middle")
                .style("fill", row.textColor)
                .style("font-size", "13px")
                .style("font-weight", "700")
                .text(row.label);

            appendMetricShareDisplay(groupNode, row.leftShareDisplay, {
                x: LEFT_X + 10,
                y: valueY,
                color: row.textColor,
                fontSize: "11.5px",
                fontWeight: "600",
                secondaryText: row.leftText,
            });

            if (provenanceSegmentHeight > 0.5) {
                const provenanceRail = leftNodes.append("g")
                    .attr("aria-label", labels.provenanceLabel);
                provenanceSegments.forEach((segment, segmentIndex) => {
                    const segmentY = provenanceRailY
                        + segmentIndex * (provenanceSegmentHeight + PROVENANCE_RAIL_GAP);
                    const provenanceControl = bindProvenancePresentation(
                        provenanceRail.append("g"),
                        segment,
                    )
                        .attr("role", "button")
                        .attr("tabindex", 0)
                        .attr("focusable", true)
                        .attr("aria-label", provenanceAriaLabel(segment))
                        .attr("aria-controls", PROVENANCE_CALLOUT_ID)
                        .attr("aria-expanded", "false")
                        .attr("aria-haspopup", "dialog")
                        .attr("data-calculator-provenance-segment-id", segment.segmentId)
                        .style("cursor", "pointer");
                    provenanceControl.append("rect")
                        .attr("class", "fishy-sankey-provenance-hit")
                        .attr(
                            "x",
                            provenanceRailX
                                - (PROVENANCE_RAIL_HIT_WIDTH - PROVENANCE_RAIL_WIDTH) / 2,
                        )
                        .attr("y", segmentY)
                        .attr("width", PROVENANCE_RAIL_HIT_WIDTH)
                        .attr("height", provenanceSegmentHeight)
                        .style("fill", "transparent")
                        .style("pointer-events", "all");
                    provenanceControl.append("rect")
                        .attr("class", "fishy-sankey-provenance-segment")
                        .attr("x", provenanceRailX)
                        .attr("y", segmentY)
                        .attr("width", PROVENANCE_RAIL_WIDTH)
                        .attr("height", provenanceSegmentHeight)
                        .attr("rx", Math.min(PROVENANCE_RAIL_WIDTH / 2, 3))
                        .attr("ry", Math.min(PROVENANCE_RAIL_WIDTH / 2, 3))
                        .style("fill", segment.color)
                        .style("pointer-events", "none")
                        .style("opacity", segment.active ? 1 : 0.65);
                });
            }

            if (hasCondition) {
                const condition = groupNode.append("text")
                    .attr("x", LEFT_X + 10)
                    .attr("y", mid + 15)
                    .attr("dominant-baseline", "middle")
                    .style("fill", row.textColor)
                    .style("font-size", "9.5px")
                    .style("font-weight", "600")
                    .style("opacity", 0.82)
                    .attr("aria-label", row.conditionText)
                    .text(truncateSankeyText(row.conditionText));
            }
        });

        const speciesConnectors = svg.append("g");
        const rightNodes = svg.append("g");
        speciesRows.forEach((row, index) => {
            const leftLayout = leftSpeciesLayout[index];
            const rightLayout = rightSpeciesLayout[index];
            const barTop = TOP_PADDING + leftLayout.top;
            const barHeight = leftLayout.height;
            const labelTop = speciesLabelTop[index];
            const connectorTop = labelTop + SPECIES_BOX_CONNECTOR_INSET;
            const connectorHeight = RIGHT_LABEL_HEIGHT - SPECIES_BOX_CONNECTOR_INSET * 2;
            const speciesRightTop = TOP_PADDING + rightLayout.top;
            const rightHeight = rightLayout.height;

            rightNodes.append("rect")
                .attr("data-calculator-row-id", row.rowId)
                .attr("x", leftBarX)
                .attr("y", barTop)
                .attr("width", RIGHT_BAR_WIDTH)
                .attr("height", barHeight)
                .attr("rx", Math.min(NODE_RADIUS, RIGHT_BAR_WIDTH / 2))
                .attr("ry", Math.min(NODE_RADIUS, RIGHT_BAR_WIDTH / 2))
                .style("fill", row.groupFillColor)
                .style("stroke", row.groupStrokeColor)
                .style("stroke-width", 1.25);

            speciesConnectors.append("path")
                .attr("data-calculator-row-id", row.rowId)
                .attr(
                    "d",
                    sankeyPath(
                        leftBarX + RIGHT_BAR_WIDTH,
                        barTop,
                        labelX,
                        connectorTop,
                        barHeight,
                        connectorHeight,
                    ),
                )
                .style("fill", row.groupConnectorColor)
                .style("opacity", 0.38);

            speciesConnectors.append("path")
                .attr("data-calculator-row-id", row.rowId)
                .attr(
                    "d",
                    sankeyPath(
                        labelX + RIGHT_LABEL_WIDTH,
                        connectorTop,
                        rightBarX,
                        speciesRightTop,
                        connectorHeight,
                        rightHeight,
                    ),
                )
                .style("fill", row.groupConnectorColor)
                .style("opacity", 0.34);

            const host = rightNodes.append("foreignObject")
                .attr("x", labelX)
                .attr("y", labelTop)
                .attr("width", RIGHT_LABEL_WIDTH)
                .attr("height", RIGHT_LABEL_HEIGHT)
                .attr("data-calculator-row-id", row.rowId)
                .append("xhtml:div")
                .attr("class", "fishy-loot-sankey-item-host")
                .html(lootSankeyItemMarkup(row, labels));
            const hostNode = host.node();
            const leadingMetric = hostNode?.querySelector?.(
                "[data-fishy-loot-row-leading-metric]",
            );
            const trailingMetric = hostNode?.querySelector?.(
                "[data-fishy-loot-row-trailing-metric]",
            );
            if (leadingMetric && row.leftBreakdown) {
                setStatBreakdownPresentation(leadingMetric, row.leftBreakdown);
            }
            if (trailingMetric && row.rightBreakdown) {
                setStatBreakdownPresentation(trailingMetric, row.rightBreakdown);
            }
        });

        const rightBarNodes = svg.append("g");
        speciesRows.forEach((row, index) => {
            const rightLayout = rightSpeciesLayout[index];
            const top = TOP_PADDING + rightLayout.top;
            const heightValue = rightLayout.height;

            rightBarNodes.append("rect")
                .attr("data-calculator-row-id", row.rowId)
                .attr("x", rightBarX)
                .attr("y", top)
                .attr("width", RIGHT_BAR_WIDTH)
                .attr("height", heightValue)
                .attr("rx", Math.min(NODE_RADIUS, RIGHT_BAR_WIDTH / 2))
                .attr("ry", Math.min(NODE_RADIUS, RIGHT_BAR_WIDTH / 2))
                .style("fill", row.groupFillColor)
                .style("stroke", row.groupStrokeColor)
                .style("stroke-width", 1.25);
        });

        const rightGroups = svg.append("g");
        rows.forEach((row) => {
            const top = rightGroupTop.get(row.rowId);
            const heightValue = rightGroupHeights.get(row.rowId);
            const mid = top + heightValue / 2;
            const hasCondition = Boolean(row.conditionText);
            const labelY = mid - (hasCondition ? 15 : 8);
            const valueY = mid + (hasCondition ? 1 : 10);

            const groupNode = bindMetricBreakdown(
                rightGroups.append("g").attr("data-calculator-row-id", row.rowId),
                row.rightBreakdown,
                `${row.label} · ${metricDisplayAriaText(
                    row.rightShareDisplay,
                    labels.rateBasisLabels,
                )}, ${labels.rightMetricLabel}: ${row.rightText}`,
            );

            groupNode.append("rect")
                .attr("x", rightGroupX)
                .attr("y", top)
                .attr("width", RIGHT_GROUP_WIDTH)
                .attr("height", heightValue)
                .attr("rx", NODE_RADIUS)
                .attr("ry", NODE_RADIUS)
                .style("fill", row.fillColor)
                .style("stroke", row.strokeColor)
                .style("stroke-width", 1.5);

            groupNode.append("text")
                .attr("x", rightGroupX + 10)
                .attr("y", labelY)
                .attr("dominant-baseline", "middle")
                .style("fill", row.textColor)
                .style("font-size", "13px")
                .style("font-weight", "700")
                .text(row.label);

            appendMetricShareDisplay(groupNode, row.rightShareDisplay, {
                x: rightGroupX + 10,
                y: valueY,
                color: row.textColor,
                fontSize: "11.5px",
                fontWeight: "600",
                secondaryText: row.rightCompactText,
            });

            if (hasCondition) {
                const condition = groupNode.append("text")
                    .attr("x", rightGroupX + 10)
                    .attr("y", mid + 15)
                    .attr("dominant-baseline", "middle")
                    .style("fill", row.textColor)
                    .style("font-size", "9.5px")
                    .style("font-weight", "600")
                    .style("opacity", 0.82)
                    .attr("aria-label", row.conditionText)
                    .text(truncateSankeyText(row.conditionText));
            }

        });

        this.replaceRenderedChildren(svg.node());
    }
}

export function registerLootSankey() {
    if (window.customElements.get("fishy-loot-sankey")) {
        return;
    }
    window.customElements.define("fishy-loot-sankey", FishyLootSankey);
}
